import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';

// ── i18n ───────────────────────────────────────────────────
const UI_TEXT = {
  IT: {
    about:       'Chi siamo',
    heading:     'Crea la tua Password',
    fieldLabel:  'Nuova Password',
    placeholder: 'Almeno 8 caratteri',
    btnLoading:  'Salvataggio...',
    btnSubmit:   'Attiva Account',
    successMsg:  'Password salvata con successo! Reindirizzamento al login...',
    errorSession:'Sessione scaduta o non valida. Richiedi un nuovo link.',
  },
  EN: {
    about:       'About',
    heading:     'Create your Password',
    fieldLabel:  'New Password',
    placeholder: 'At least 8 characters',
    btnLoading:  'Saving...',
    btnSubmit:   'Activate Account',
    successMsg:  'Password saved! Redirecting to login...',
    errorSession:'Session expired or invalid. Please request a new link.',
  },
  ES: {
    about:       'Acerca de',
    heading:     'Crea tu contraseña',
    fieldLabel:  'Nueva contraseña',
    placeholder: 'Al menos 8 caracteres',
    btnLoading:  'Guardando...',
    btnSubmit:   'Activar cuenta',
    successMsg:  '¡Contraseña guardada! Redirigiendo al inicio de sesión...',
    errorSession:'Sesión expirada o no válida. Por favor, solicita un nuevo enlace.',
  },
  DE: {
    about:       'Über uns',
    heading:     'Passwort erstellen',
    fieldLabel:  'Neues Passwort',
    placeholder: 'Mindestens 8 Zeichen',
    btnLoading:  'Wird gespeichert...',
    btnSubmit:   'Konto aktivieren',
    successMsg:  'Passwort gespeichert! Weiterleitung zur Anmeldung...',
    errorSession:'Sitzung abgelaufen oder ungültig. Bitte fordern Sie einen neuen Link an.',
  },
  FR: {
    about:       'À propos',
    heading:     'Créez votre mot de passe',
    fieldLabel:  'Nouveau mot de passe',
    placeholder: 'Au moins 8 caractères',
    btnLoading:  'Enregistrement...',
    btnSubmit:   'Activer le compte',
    successMsg:  'Mot de passe enregistré ! Redirection vers la connexion...',
    errorSession:'Session expirée ou invalide. Veuillez demander un nouveau lien.',
  },
  PL: {
    about:       'O nas',
    heading:     'Utwórz swoje hasło',
    fieldLabel:  'Nowe hasło',
    placeholder: 'Co najmniej 8 znaków',
    btnLoading:  'Zapisywanie...',
    btnSubmit:   'Aktywuj konto',
    successMsg:  'Hasło zapisane! Przekierowanie do logowania...',
    errorSession:'Sesja wygasła lub jest nieprawidłowa. Poproś o nowy link.',
  },
}

function detectLang() {
  const lang = (navigator.language || '').toLowerCase()
  if (lang.startsWith('pl')) return 'PL'
  if (lang.startsWith('en')) return 'EN'
  if (lang.startsWith('es')) return 'ES'
  if (lang.startsWith('de')) return 'DE'
  if (lang.startsWith('fr')) return 'FR'
  return 'IT'
}

export default function ConfirmPassword() {
  const t = UI_TEXT[detectLang()]
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState(null);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  async function handleSetPassword(e) {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const { data: { session } } = await supabase.auth.getSession();
      
      if (!session) throw new Error(t.errorSession);

      const response = await fetch('/api/set-password', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`
        },
        body: JSON.stringify({ password })
      });

      const result = await response.json();
      if (result.error) throw new Error(result.error);

      setMessage(t.successMsg);
      
      // Give the user 2 seconds to read the success message
      setTimeout(() => {
        navigate('/login');
      }, 2000);

    } catch (err) {
      setError(err.message);
      setLoading(false);
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#0f0a05] px-4">
      <div className="max-w-md w-full bg-[#1a1209] border border-[#3d2d14] p-10 rounded-[2.5rem] shadow-2xl">
        <h2 className="text-3xl font-serif font-bold text-[#f5f0e8] mb-8 text-center">{t.heading}</h2>
        
        {error && <div className="mb-6 p-4 bg-red-900/20 text-red-200 rounded-xl text-sm border border-red-900/50">{error}</div>}
        
        {message ? (
          <div className="p-6 bg-[#c9922a]/10 border border-[#c9922a]/30 text-[#f5f0e8] rounded-2xl text-center animate-pulse">
            {message}
          </div>
        ) : (
          <form onSubmit={handleSetPassword} className="space-y-6">
            <div>
              <label className="block text-[10px] font-bold text-[#c4b89a] uppercase tracking-widest mb-2 ml-2">{t.fieldLabel}</label>
              <input
                type="password"
                placeholder={t.placeholder}
                className="w-full bg-[#0f0a05] border border-[#3d2d14] rounded-xl p-4 text-[#f5f0e8] focus:border-[#c9922a] outline-none transition-all"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
            </div>
            <button 
              disabled={loading}
              className="w-full bg-[#c9922a] hover:bg-[#e5b050] text-[#1a1209] font-bold py-5 rounded-xl transition-all shadow-lg disabled:opacity-50 uppercase tracking-widest"
            >
              {loading ? t.btnLoading : t.btnSubmit}
            </button>
          </form>
        )}
        <div className="mt-8 pt-6 border-t border-[#3d2d14] text-center">
          <a href="/about" className="text-xs text-[#c4b89a] hover:text-[#c9922a] transition-colors uppercase tracking-widest font-bold">
            {t.about}
          </a>
          <span className="text-xs text-[#3d2d14] mx-2">·</span>
          <a href="/privacy" className="text-xs text-[#c4b89a] hover:text-[#c9922a] transition-colors">Privacy Policy</a>
        </div>
      </div>
    </div>
  );
}